import React from "react"
import { StaticImage } from "gatsby-plugin-image"
import Icon from "../components/icon"

export const pageTitle = "Emergency Lighting Compliance With Confidence"
export const pageMetadata =
  "The pushfusion emergency lighting compliance module gives you a single pane of glass to manage your entire estate from anywhere. You can identify and resolve compliance issues in your estate quicker than ever before."
export const title = "Emergency lighting"
export const subtitle = "just got smarter"
export const description = [
  {
    bold: false,
    text: (
      <>
        <span className="text-blue-50 font-bold">push</span>
        <span className="text-pushfusion font-extrabold">fusion</span> provides
        a single secure dashboard to manage emergency lighting compliance for
        your entire estate.
      </>
    ),
  },
  {
    bold: false,
    text: "With a mobile app designed specifically for engineers onsite, pushfusion gives you the power to harmonise your administrative and operational workflow to identify and resolve compliance issues quicker than ever before.",
  },
]
export const features = [
  {
    name: "Test Result",
    icon: (
      <Icon
        className="h-16 w-16 text-blue-50"
        aria-hidden="true"
        color="#0E98FA"
        icon="test"
      />
    ),
    text: "An overview of emergency lighting compliance for your entire estate in one place",
  },
  {
    name: "Investigate",
    icon: (
      <Icon
        className="h-16 w-16 text-blue-50"
        aria-hidden="true"
        color="#0E98FA"
        icon="investigation"
      />
    ),
    text: "The ability to drill down and view the full details of your test history",
  },
  {
    name: "Job List",
    icon: (
      <Icon
        className="h-16 w-16 text-blue-50"
        aria-hidden="true"
        color="#0E98FA"
        icon="listing"
      />
    ),
    text: "Job lists that allow engineers to keep track of their workload",
  },
]

export const tabs = [
  {
    name: "Buildings",
    features: [
      {
        name: "Dual views on the compliance status of the estate",
        description: [
          "The building summary is designed to give a quick, real-time overview of emergency lighting health at each of your building locations.",
          "Each site is represented with a single icon, allowing you to quickly identify sites with compliance issues that need attention.",
          "Centred on your current location, the map view shows a single marker icon that represents the current overall emergency lighting health for each building.",
        ],
        imageSrc: (
          <StaticImage
            src="../assets/images/emergency/building-list.png"
            alt="2 mobile screens showing how pushfusion app can display building locations, on a map or a list view."
            placeholder="tracedSVG"
            layout="constrained"
            quality={100}
            width={683}
            loading="eager"
            aspectRatio={16 / 9}
          />
        ),
      },
    ],
  },
  {
    name: "Overview",
    features: [
      {
        name: "Detailed information about the sites in your estate",
        description: [
          "View a summary of each building in your estate to see compliance status, last update and test dates allowing you to drill into the information quickly and with minimum fuss.",
          "Access supplementary data including building info, last test data, luminaire health, and quickly switch to test/event failures and job list details.",
        ],
        imageSrc: (
          <StaticImage
            src="../assets/images/emergency/building-overview.png"
            alt="Building overview of pushfusion"
            placeholder="tracedSVG"
            layout="constrained"
            quality={100}
            width={683}
            loading="eager"
            aspectRatio={16 / 9}
          />
        ),
      },
    ],
  },
  {
    name: "Failures",
    features: [
      {
        name: "Reporting by exception to show detailed fault information",
        description: [
          <span>
            <span className="text-gray-900 font-bold">push</span>
            <span className="text-pushfusion font-extrabold">fusion</span> holds
            the historical data for all your buildings, this powers the mobile
            app and gives field engineers the ability to review building history
            and gain deeper insight whilst in attendance.
          </span>,
          "Use the search feature by test type to drill down by faults and failed tests.",
          "Full failure details about devices and their locations within a building are displayed, this enables engineers to meet their targets by guiding them to device failures for rapid resolution.",
        ],
        imageSrc: (
          <StaticImage
            src="../assets/images/emergency/building-failures.png"
            alt="pushfusion mobile showing emergency failures of a building"
            placeholder="tracedSVG"
            layout="constrained"
            quality={100}
            width={683}
            loading="eager"
            aspectRatio={16 / 9}
          />
        ),
      },
    ],
  },
  {
    name: "Job List",
    features: [
      {
        name: "Stay organised onsite with job lists",
        description: [
          "The job list tool helps field engineers stay organised when attending a building. They can easily keep track of their workload and stay on top of the failures they are responsible for.",
          "As failures are addressed, the jobs allocated for each building will be updated and resolved so the field engineer can move on to the next job with confidence.",
        ],
        imageSrc: (
          <StaticImage
            src="../assets/images/emergency/joblist.png"
            alt="pushfusion mobile app showing how an engineer can use a job list to stay organised"
            placeholder="tracedSVG"
            layout="constrained"
            quality={100}
            width={683}
            loading="eager"
            aspectRatio={16 / 9}
          />
        ),
      },
    ],
  },
]
